/**
 * Gestion › Présences : les écrans des animateurs, pour la coordination, avec
 * tous les enfants du centre (décidé le 25/10/2026).
 *
 * La coordination choisit un centre et une semaine ; les tuiles, la feuille
 * Présences | À savoir, la fiche de l'enfant et les photos sont ceux du lien
 * des animateurs (`components/presences/EcransDesPresences.tsx`), nourris par
 * sa session (`lib/presences/source.session.ts`). En plus : le règlement de
 * chaque inscription colore le bord de la ligne (vert payé, ambre à payer),
 * et le bouton « Liens pour les anims » crée les liens de la semaine.
 *
 * L'ancien écran (le tableau de la semaine, la sauvegarde groupée) reste
 * joignable par « Ancien écran » le temps de la transition.
 *
 * Depuis le 01/11/2026, le même écran a deux faces : « Centres de vacances »
 * (ci-dessus) et « Garderie (écoles) » — la semaine d'une école, grille
 * enfants × jours × plages, à cocher ou décocher depuis le bureau
 * (`PresencesGarderie`).
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Building, Calendar, Link2, Loader2, TableProperties } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { EcransDesPresences } from '../../../components/presences/EcransDesPresences'
import type { NavigationParams } from '../../App'
import { lundiDe, semaineEnClair } from '../../../lib/presences/lien'
import { semaineDesParametres, semaineProposee, type SemaineDuCentre } from '../../../lib/presences/session'
import { lireLesCentres, lireLesSemaines, sourceDeLaSession, type CentreDePresences } from '../../../lib/presences/source.session'
import type { Vue } from '../../../lib/presences/source'
import { lignesDe, reglementDeLInscription, type ReglementAffiche } from '../../../lib/inscriptions/reglement'
import { lireReglementDesInscriptions } from '../../../lib/inscriptions/reglement.supabase'
import { LiensPresences } from './LiensPresences'
import { LiensGarderie } from './LiensGarderie'
import { PresencesGarderie } from './PresencesGarderie'

type Face = 'centres' | 'garderie'
const CLE_FACE = 'presences-du-centre-face'
const lireFace = (): Face => {
  try {
    return sessionStorage.getItem(CLE_FACE) === 'garderie' ? 'garderie' : 'centres'
  } catch {
    return 'centres'
  }
}

const aujourdHui = (): string => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/* Le dernier choix (centre, lundi), gardé le temps de la session du navigateur. */
const CLE_CHOIX = 'presences-du-centre-choix'
const lireChoix = (): { centreId: string; lundi: string } | null => {
  try {
    const brut = sessionStorage.getItem(CLE_CHOIX)
    return brut ? (JSON.parse(brut) as { centreId: string; lundi: string }) : null
  } catch {
    return null
  }
}
const garderChoix = (centreId: string, lundi: string) => {
  try {
    sessionStorage.setItem(CLE_CHOIX, JSON.stringify({ centreId, lundi }))
  } catch {
    /* rien : on redemandera */
  }
}

interface Props {
  initialParams?: NavigationParams | null
  onParamsConsumed?: () => void
  /** Vers l'ancien écran, le temps de la transition. */
  onAncienEcran?: () => void
  /** Vers la liste du jour d'une école de garderie (Gestion › Pointer la garderie). */
  onPointerLaGarderie?: (params: NavigationParams) => void
}

export function PresencesDuCentre({ initialParams, onParamsConsumed, onAncienEcran, onPointerLaGarderie }: Props) {
  const { user } = useAuth()
  const [centres, setCentres] = useState<CentreDePresences[]>([])
  const [centreId, setCentreId] = useState<string>(() => initialParams?.centerId ?? lireChoix()?.centreId ?? '')
  const [semaines, setSemaines] = useState<SemaineDuCentre[] | null>(null)
  const [lundi, setLundi] = useState<string>('')
  const [vue, setVue] = useState<Vue>({ nom: 'groupes' })
  const [erreur, setErreur] = useState<string | null>(null)
  const [liensOuverts, setLiensOuverts] = useState(false)
  const [liensGarderieOuverts, setLiensGarderieOuverts] = useState(false)
  const [face, setFace] = useState<Face>(() => (initialParams?.centerId ? 'centres' : lireFace()))

  useEffect(() => {
    try {
      sessionStorage.setItem(CLE_FACE, face)
    } catch {
      /* rien */
    }
  }, [face])

  useEffect(() => {
    lireLesCentres()
      .then(setCentres)
      .catch((e) => setErreur(e instanceof Error ? e.message : 'Impossible de lire les centres'))
  }, [])

  /* Le centre change : ses semaines, puis celle à ouvrir (tuile du tableau de bord, dernier choix, ou aujourd'hui). */
  useEffect(() => {
    if (!centreId) {
      setSemaines(null)
      setLundi('')
      return
    }
    let annule = false
    setSemaines(null)
    setErreur(null)
    lireLesSemaines(centreId)
      .then((s) => {
        if (annule) return
        setSemaines(s)
        const parametres = initialParams ? semaineDesParametres(s, initialParams) : null
        const choix = lireChoix()
        const gardee = choix && choix.centreId === centreId ? s.find((x) => x.lundi === choix.lundi) : null
        const ouverte = parametres ?? gardee ?? semaineProposee(s, aujourdHui())
        setLundi(ouverte?.lundi ?? '')
        setVue({ nom: 'groupes' })
        if (parametres) onParamsConsumed?.()
      })
      .catch((e) => {
        if (!annule) setErreur(e instanceof Error ? e.message : 'Impossible de lire les semaines')
      })
    return () => {
      annule = true
    }
    // Les paramètres du tableau de bord ne servent qu'à l'arrivée.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [centreId])

  useEffect(() => {
    if (centreId && lundi) garderChoix(centreId, lundi)
  }, [centreId, lundi])

  const centre = centres.find((c) => c.id === centreId) ?? null
  const semaine = semaines?.find((s) => s.lundi === lundi) ?? null

  const source = useMemo(
    () => (centre && semaine ? sourceDeLaSession({ centre, semaine, userId: user?.id ?? null }) : null),
    [centre, semaine, user?.id],
  )

  const lireReglements = useCallback(async (ids: string[]): Promise<Map<string, ReglementAffiche>> => {
    const carte = await lireReglementDesInscriptions(ids)
    const statuts = new Map<string, ReglementAffiche>()
    for (const id of ids) {
      statuts.set(id, reglementDeLInscription({ annulation: null, commande: null }, lignesDe(carte, id)))
    }
    return statuts
  }, [])

  const lienVers = useCallback(() => null, [])

  return (
    <div className="space-y-4">
      {liensOuverts && <LiensPresences onClose={() => setLiensOuverts(false)} />}
      {liensGarderieOuverts && <LiensGarderie onClose={() => setLiensGarderieOuverts(false)} />}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{face === 'garderie' ? 'Présences de la garderie' : 'Présences du centre'}</h1>
          <p className="text-gray-600 text-sm sm:text-base">
            {face === 'garderie' ? 'La semaine d’une école : qui était là, quel jour, à quelle plage' : 'Les mêmes écrans que les animateurs, pour tout le centre'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setLiensOuverts(true)}
            className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            title="Un lien par centre pour que les animateurs pointent sur leur téléphone"
          >
            <Link2 className="h-4 w-4" />
            <span className="hidden sm:inline">Liens pour les anims</span>
            <span className="sm:hidden">Liens</span>
          </button>
          <button
            onClick={() => setLiensGarderieOuverts(true)}
            className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            title="Un lien par école pour que les animateurs pointent la garderie sur leur téléphone"
          >
            <Link2 className="h-4 w-4" />
            <span className="hidden sm:inline">Liens garderie</span>
            <span className="sm:hidden">Garderie</span>
          </button>
          {onAncienEcran && (
            <button
              onClick={onAncienEcran}
              className="flex items-center gap-1 sm:gap-2 px-3 py-2 sm:px-4 text-gray-500 hover:text-gray-800 text-sm"
              title="Le tableau de la semaine, le temps de la transition"
            >
              <TableProperties className="h-4 w-4" />
              <span className="hidden sm:inline">Ancien écran</span>
            </button>
          )}
        </div>
      </div>

      {/* Les deux faces : les centres de vacances, la garderie des écoles */}
      <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden text-sm">
        <button
          type="button"
          onClick={() => setFace('centres')}
          className={`px-4 py-2 ${face === 'centres' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          Centres de vacances
        </button>
        <button
          type="button"
          onClick={() => setFace('garderie')}
          className={`px-4 py-2 border-l border-gray-300 ${face === 'garderie' ? 'bg-primary-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          Garderie (écoles)
        </button>
      </div>

      {face === 'garderie' && <PresencesGarderie onPointer={onPointerLaGarderie} />}

      {/* Le centre et la semaine */}
      {face === 'centres' && (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-2">
              <Building className="h-4 w-4 inline mr-1" />
              Centre
            </span>
            <select
              value={centreId}
              onChange={(e) => {
                setCentreId(e.target.value)
                setLundi('')
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Sélectionner un centre</option>
              {centres.map((c) => (
                <option key={c.id} value={c.id}>{c.nom}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-sm font-medium text-gray-700 mb-2">
              <Calendar className="h-4 w-4 inline mr-1" />
              Semaine
            </span>
            <select
              value={lundi}
              onChange={(e) => {
                setLundi(e.target.value)
                setVue({ nom: 'groupes' })
              }}
              disabled={!centreId || semaines === null}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100"
            >
              {!centreId && <option value="">Choisissez d’abord un centre</option>}
              {centreId && semaines === null && <option value="">Chargement…</option>}
              {centreId && semaines !== null && semaines.length === 0 && <option value="">Aucun stage dans ce centre</option>}
              {(semaines ?? []).map((s) => (
                <option key={s.lundi} value={s.lundi}>
                  {s.libelle} · {semaineEnClair(s.lundi, s.vendredi)}
                  {s.lundi === lundiDe(aujourdHui()) ? ' · cette semaine' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        {erreur && <p className="mt-3 text-sm text-red-700">{erreur}</p>}
      </div>
      )}

      {face === 'centres' && centreId && semaines === null && !erreur && (
        <div className="flex justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      )}

      {face === 'centres' && source && (
        <EcransDesPresences
          key={`${centreId}:${lundi}`}
          source={source}
          vue={vue}
          lienVers={lienVers}
          naviguer={setVue}
          lireReglements={lireReglements}
          toutLeCentre
          hautCollant="top-14 sm:top-16"
          pleinePage={false}
        />
      )}
    </div>
  )
}
